import { Dialog } from 'primereact/dialog';
import { Card } from 'primereact/card';
import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import SelectedAccountLineInfo from './SelectedAccountLineInfo';
import apollo from '../../../services/apollo';
import { HIERARCHY_ACCOUNT } from './hierachyQueries';
import { CreditPlan } from '../../../shared/Constants';
import { Loading } from '../../../components/Loading';
import { getCreditPlanDisplayName } from '../../../utils/AppUtil';

const messages = defineMessages({
  accountType: {
    id: 'lbl.account.type',
    defaultMessage: 'Account type',
  },
  accountId: {
    id: 'lbl.account.id',
    defaultMessage: 'Account id',
  },
  plan: {
    id: 'lbl.plan',
    defaultMessage: 'Plan',
  },
  subscriptionStartDate: {
    id: 'lbl.subscription-start-date',
    defaultMessage: 'Subscription starts on',
  },
  subscriptionEndDate: {
    id: 'lbl.subscription-end-date',
    defaultMessage: 'Subscription ends on',
  },
  ownerName: {
    id: 'lbl.owner.name',
    defaultMessage: 'Owner Name',
  },
  ownerEmail: {
    id: 'lbl.owner.email',
    defaultMessage: 'Owner Email',
  },
  noOfUsers: {
    id: 'lbl.noOfUsers',
    defaultMessage: 'Number of users',
  },
  externalSupport: {
    id: 'lbl.external.support',
    defaultMessage: 'External Support',
  },
  creditsBalance: {
    id: 'lbl.credits.balance',
    defaultMessage: 'Credits Balance',
  },
  superMatchCreditsBalance: {
    id: 'lbl.super-match-credits.balance',
    defaultMessage: 'SuperMatch Credits Balance',
  },
  latestCreditsConsumption: {
    id: 'lbl.latest.credits.consumption',
    defaultMessage: 'Latest Credits Consumption',
  },
  demoCreditsTransferred: {
    id: 'lbl.demo.credits.transferred',
    defaultMessage: 'Demo Credits Transferred',
  },
  standard: {
    id: 'dashboard.dashboard-account-information.standard-credits-consumption',
    defaultMessage: 'Standard credits consumption',
  },
  unlimited: {
    id: 'dashboard.dashboard-account-information.unlimited-credits',
    defaultMessage: 'Unlimited',
  },
  noLimit: {
    id: 'dashboard.dashboard-account-information.no-limit',
    defaultMessage: 'NoLimit',
  },
});

const DEFAULT_DATA_STATE = {
  id: null,
  isNewBusinessModelAccount: false,
  demoTransferedCredits: false,
  isExternalSupport: false,
  latestCreditsConsumption: null,
  numberOfUsers: 0,
  ownerName: '',
  ownerEmail: '',
  plan: CreditPlan.STANDARD,
  subscriptionStart: null,
  subscriptionEnd: null,
  creditsBalance: null,
  superMatchCreditsBalance: null,
};

const NodeSelectedModal = ({ selectedNode, onHide, canSeeDemoCreditsTransferred }) => {
  const [data, setData] = useState(DEFAULT_DATA_STATE);

  const { formatMessage } = useIntl();

  const loading = data === DEFAULT_DATA_STATE;

  useEffect(() => {
    fetchAccountDetails();
    // eslint-disable-next-line
  }, [selectedNode]);

  const fetchAccountDetails = async () => {
    const result = await apollo.query({ query: HIERARCHY_ACCOUNT, variables: { id: selectedNode.id } });

    setData({
      ...selectedNode,
      ...result.data.accountHierarchyDetails,
    });
  };

  if (loading) return <Loading />;

  const creditPlanDisplayName = getCreditPlanDisplayName(
    data.plan,
    formatMessage(messages.standard),
    formatMessage(messages.unlimited),
    formatMessage(messages.noLimit),
  );

  return (
    <Dialog
      header={data.brandDisplayName}
      visible={!loading}
      style={{ width: '50vw', textAlign: 'center' }}
      modal
      onHide={onHide}
    >
      <Card>
        <SelectedAccountLineInfo description={formatMessage(messages.accountType)} value={data.accountType || 'N/A'} />
        <br />
        <SelectedAccountLineInfo description={formatMessage(messages.accountId)} value={data.id || 'N/A'} />
        <br />
        <SelectedAccountLineInfo description={formatMessage(messages.plan)} value={creditPlanDisplayName} />
        <br />
        {data.isNewBusinessModelAccount && (
          <>
            <SelectedAccountLineInfo
              description={formatMessage(messages.subscriptionStartDate)}
              value={data.subscriptionStart || 'N/A'}
            />
            <br />
            <SelectedAccountLineInfo
              description={formatMessage(messages.subscriptionEndDate)}
              value={data.subscriptionEnd || 'N/A'}
            />
            <br />
          </>
        )}
        <SelectedAccountLineInfo description={formatMessage(messages.ownerName)} value={data.ownerName || 'N/A'} />
        <br />
        <SelectedAccountLineInfo description={formatMessage(messages.ownerEmail)} value={data.ownerEmail || 'N/A'} />
        <br />
        <SelectedAccountLineInfo description={formatMessage(messages.noOfUsers)} value={data.numberOfUsers || 'N/A'} />
        <br />
        <SelectedAccountLineInfo
          description={formatMessage(messages.externalSupport)}
          value={data.isExternalSupport ? 'YES' : 'NO'}
        />
        <br />
        {data.isNewBusinessModelAccount ? (
          <SelectedAccountLineInfo
            description={formatMessage(messages.superMatchCreditsBalance)}
            value={data.superMatchCreditsBalance || 'N/A'}
          />
        ) : (
          <SelectedAccountLineInfo
            description={formatMessage(messages.creditsBalance)}
            value={data.creditsBalance || 'N/A'}
          />
        )}
        <br />
        {!data.isNewBusinessModelAccount && (
          <>
            <SelectedAccountLineInfo
              description={formatMessage(messages.latestCreditsConsumption)}
              value={data.latestCreditsConsumption || 'N/A'}
            />
            <br />
          </>
        )}
        {canSeeDemoCreditsTransferred && (
          <SelectedAccountLineInfo
            description={formatMessage(messages.demoCreditsTransferred)}
            value={data.demoCreditsTransferred ? 'YES' : 'NO'}
          />
        )}
      </Card>
    </Dialog>
  );
};

NodeSelectedModal.propTypes = {
  canSeeDemoCreditsTransferred: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  selectedNode: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default NodeSelectedModal;
